import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, CreditCard } from "lucide-react";
import { SiGitconnected } from "react-icons/si";
import { VscDebugDisconnect } from "react-icons/vsc";
import { FiCheck, FiArrowLeft, FiLock, FiPhone } from "react-icons/fi";
import { PhoneField } from "../../components/PhoneField/PhoneField";
import instagram from "../../assets/instagram_onb.png";
import facebook from "../../assets/facebook_onb.png";
import whatsapp from "../../assets/whatsapp_onb.png";
import telegram from "../../assets/telegram_onb.png";
import calendar from "../../assets/google_calendar_onb.png";
import { REST_API_URL } from "../../api/config";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import { selectCheckPayment } from "../../redux/slices/paymentSlice/PaymentSlice";
import { getLocationThunk } from "../../redux/slices/profileSlice/asyncthunk";
import { selectLocationCode } from "../../redux/slices/profileSlice/ProfileSlice";
import { useTranslation } from "react-i18next";
import { buildHeaders } from "../../api/buildHeaders";
import toast from "react-hot-toast";
import { deleteConnectWhatsAppDisconnect } from "../../api/connections/connectService";
import { ConnectWhatsAppModal } from "../../components/modals/connectWhatsAppModal";

const API = REST_API_URL;

const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

async function authFetch(url, options = {}) {
  let access =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  let refresh =
    localStorage.getItem("refreshToken") ||
    sessionStorage.getItem("refreshToken");

  const doRequest = async (token) => {
    const headers = buildHeaders(options.headers || {});
    return fetch(url, { ...options, headers });
  };

  if (!access) return { status: 401, ok: false, json: async () => ({}) };

  let response = await doRequest(access);
  if (response.status !== 401) return response;
  if (!refresh) return response;

  const refreshHeaders = {
    "Content-Type": "application/json",
    "User-Timezone": getUserTimeZone(),
  };
  const refreshRes = await fetch(`${API}/auth/token/refresh/`, {
    method: "POST",
    headers: refreshHeaders,
    body: JSON.stringify({ refresh }),
  });
  const refreshData = await refreshRes.json().catch(() => ({}));

  if (refreshRes.ok && refreshData.access) {
    access = refreshData.access;
    localStorage.setItem("accessToken", access);
    return doRequest(access);
  }

  if (!refreshRes.ok) {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
  }

  return response;
}

function InstagramIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img
          src={instagram}
          alt="Instagram"
          className="w-7 h-7 object-contain"
        />
      </div>
    </div>
  );
}

function WhatsAppIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-green-500 to-green-600 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img src={whatsapp} alt="WhatsApp" className="w-7 h-7 object-contain" />
      </div>
    </div>
  );
}

function TelegramIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-blue-400 to-blue-500 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img src={telegram} alt="Telegram" className="w-7 h-7 object-contain" />
      </div>
    </div>
  );
}

function FacebookIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-blue-600 to-blue-700 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img src={facebook} alt="Facebook" className="w-7 h-7 object-contain" />
      </div>
    </div>
  );
}

function CalendarIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-blue-500 to-blue-600 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img
          src={calendar}
          alt="Google Calendar"
          className="w-7 h-7 object-contain"
        />
      </div>
    </div>
  );
}

const instagramUrl = (email) =>
  `https://www.instagram.com/oauth/authorize?enable_fb_login=0&force_authentication=1&disable_native=1&client_id=1362107724965082&redirect_uri=${API}/auth/connect-instagram&response_type=code&scope=instagram_business_basic,instagram_business_manage_messages&state=${encodeURIComponent(email)}|||web`;

const whatsappUrl = (email) =>
  `https://www.facebook.com/v23.0/dialog/oauth?display=popup&client_id=1167918065008974&redirect_uri=${encodeURIComponent("https://testing.halper.ai/api/whatsapp/connect-whatsapp/")}&config_id=1412479926475322&response_type=code&auth_type=&fallback_redirect_uri=${encodeURIComponent("https://testing.halper.ai/api/whatsapp/connect-whatsapp/")}&override_default_response_type=true&extras=%7B%22featureType%22%3A%22whatsapp_business_app_onboarding%22%2C%22sessionInfoVersion%22%3A%223%22%2C%22version%22%3A%22v3%22%2C%22features%22%3A%5B%7B%22name%22%3A%22marketing_messages_lite%22%7D%2C%7B%22name%22%3A%22app_only_install%22%7D%5D%7D&state=${encodeURIComponent(email + "|||web")}`;

const googleCalendarUrl = (email) => {
  const redirectUri = encodeURIComponent(`${window.location.origin}/profile`);
  return `${API}/calendar/google/calendar-init?state=${encodeURIComponent(email + "|||web")}&redirect_uri=${redirectUri}`;
};

function DisconnectModal({ isOpen, onClose, onConfirm, channelName }) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(isOpen);
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleConfirm = () => {
    setIsVisible(false);
    setTimeout(onConfirm, 300);
  };

  if (!isOpen && !isVisible) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 transition-all duration-300 ${isVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl transition-all duration-300 ${isVisible ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <VscDebugDisconnect className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="font-bold text-2xl text-gray-900 mb-2">
            {t("settings.disconnectModalTitle", { channelName })}
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            {t("settings.disconnectModalDesc", { channelName })}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-6 py-3.5 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-all duration-200 active:scale-[0.98]"
          >
            {t("settings.cancel")}
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-6 py-3.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-xl shadow-lg hover:shadow-red-200 transition-all duration-200 active:scale-[0.98]"
          >
            {t("settings.disconnect")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChannelsAndAppsNew() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState("");
  const [social, setSocial] = useState({
    instagram: null,
    whatsapp: null,
    googleCalendar: null,
  });
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const paymentCheck = useAppSelector(selectCheckPayment);
  const [showSubModal, setShowSubModal] = useState(false);
  const [disconnectModal, setDisconnectModal] = useState({
    isOpen: false,
    channel: null,
    channelName: "",
  });
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [isCalendarConnecting, setIsCalendarConnecting] = useState(false);
  const [telegramModal, setTelegramModal] = useState({
    isOpen: false,
    step: "phone",
    connectionId: null,
    message: "",
    needPassword: false,
  });
  const [telegramPhone, setTelegramPhone] = useState("");
  const [telegramOtp, setTelegramOtp] = useState("");
  const [telegramPassword, setTelegramPassword] = useState("");
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramError, setTelegramError] = useState(null);
  const locationCode = useAppSelector(selectLocationCode);

  const openTelegramModal = () => {
    setTelegramModal({
      isOpen: true,
      step: "phone",
      connectionId: null,
      message: "",
      needPassword: false,
    });
    setTelegramPhone("");
    setTelegramOtp("");
    setTelegramPassword("");
    setTelegramError(null);
  };

  const closeTelegramModal = () => {
    setTelegramModal((prev) => ({ ...prev, isOpen: false }));
    setTelegramError(null);
  };

  const handleTelegramPhoneSubmit = async () => {
    if (!telegramPhone.trim()) return;
    setTelegramLoading(true);
    setTelegramError(null);
    try {
      const res = await authFetch(`${API}/telegram/connections/`, {
        method: "POST",
        body: JSON.stringify({ phone_number: telegramPhone.trim() }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 201) {
        await fetchSocial();
        closeTelegramModal();
        return;
      }
      if (!res.ok) {
        setTelegramError(
          data?.error ||
            data?.message ||
            t("settings.telegramErrorInvalidPhone"),
        );
        return;
      }
      setTelegramModal((prev) => ({
        ...prev,
        step: "otp",
        connectionId: data.connection_id,
        message: data.message || "",
        needPassword: false,
      }));
    } catch {
      setTelegramError(t("settings.telegramErrorFailedRequest"));
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramOtpSubmit = async () => {
    if (telegramOtp.length < 5) return;
    setTelegramLoading(true);
    setTelegramError(null);
    try {
      const res = await authFetch(
        `${API}/telegram/connections/${telegramModal.connectionId}/verify-code/`,
        {
          method: "POST",
          body: JSON.stringify({ code: telegramOtp }),
        },
      );
      const data = await res.json().catch(() => ({}));

      if (res.status === 201) {
        await fetchSocial();
        closeTelegramModal();
        return;
      }
      if (res.status === 210 || !res.ok) {
        setTelegramError(
          data?.message ||
            data?.error ||
            t("settings.telegramErrorInvalidCode"),
        );
        setTelegramOtp("");
        return;
      }
      setTelegramModal((prev) => ({
        ...prev,
        step: "2fa",
        connectionId: data.connection_id ?? prev.connectionId,
        message: data.message || "",
      }));
      setTelegramOtp("");
    } catch {
      setTelegramError(t("settings.telegramErrorInvalidCode"));
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramPasswordSubmit = async () => {
    if (!telegramPassword.trim()) return;
    setTelegramLoading(true);
    setTelegramError(null);
    try {
      const res = await authFetch(
        `${API}/telegram/connections/${telegramModal.connectionId}/verify-2fa/`,
        {
          method: "POST",
          body: JSON.stringify({ password: telegramPassword }),
        },
      );
      if (res.ok) {
        await fetchSocial();
        closeTelegramModal();
      } else {
        const data = await res.json().catch(() => ({}));
        setTelegramError(
          data?.error || t("settings.telegramErrorWrongPassword"),
        );
      }
    } catch {
      setTelegramError(t("settings.telegramErrorFailedVerify"));
    } finally {
      setTelegramLoading(false);
    }
  };

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const param = queryParams.get("param");
    const code = queryParams.get("code");
    const error = queryParams.get("error");
    const success = queryParams.get("success");
    const calendarConnected = queryParams.get("calendar_connected");

    const isInstagramSuccess =
      location.pathname.includes("instagram-connect") && param === "success";
    const isWhatsappSuccess =
      location.pathname.includes("whatsapp-connect") && param === "success";
    const hasCalendarParams = code || error || success || calendarConnected;
    const isCalendarCallback =
      location.pathname === "/profile" && hasCalendarParams;

    if (isInstagramSuccess || isWhatsappSuccess) {
      if (typeof window.gtag === "function") {
        window.gtag("event", "social_media_added", {
          social_media_type: isInstagramSuccess ? "Instagram" : "WhatsApp",
        });
      }
      navigate(location.pathname, { replace: true });
      fetchSocial();
    }

    if (isCalendarCallback) {
      if (error) {
        console.error("Google Calendar auth error:", error);
        setIsCalendarConnecting(false);
        toast.error(t("settings.calendarConnectionFailedWithError", { error }));
      } else if (code) {
        handleGoogleCalendarCodeExchange(code);
      } else if (success === "true" || calendarConnected === "true") {
        fetchCalendarStatus();
        setIsCalendarConnecting(false);
        toast.success(t("settings.calendarConnected"));
      }

      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => fetchCalendarStatus(), 1000);
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    dispatch(getLocationThunk());

    const fetchUserData = async () => {
      const [userRes] = await Promise.all([authFetch(`${API}/auth/userinfo/`)]);
      if (userRes.ok) {
        const userData = await userRes.json();
        if (userData.user?.email) setEmail(userData.user.email);
        setUserInfo(userData);
      }
      await fetchSocial();
      await fetchCalendarStatus();
      setIsLoading(false);
    };

    fetchUserData();
  }, [dispatch]);

  const fetchSocial = async () => {
    const res = await authFetch(`${API}/auth/social-profile-view/`);
    if (res.ok) {
      const json = await res.json();
      setSocial((prev) => ({ ...prev, ...json }));
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const res = await authFetch(`${API}/calendar/google/calendar-exist/`);
      if (res.ok) {
        try {
          const data = await res.json();
          let isConnected = false;
          if (typeof data === "boolean") {
            isConnected = data;
          } else if (typeof data === "object") {
            if (data.existing !== undefined)
              isConnected = data.existing === true;
            else if (data.exists !== undefined)
              isConnected = data.exists === true;
            else if (data.connected !== undefined)
              isConnected = data.connected === true;
            else if (
              data.id !== undefined &&
              data.id !== null &&
              data.id !== ""
            )
              isConnected = true;
            else if (data.status === "connected") isConnected = true;
          }
          const status = { existing: isConnected, ...data };
          setCalendarStatus(status);
          setSocial((prev) => ({ ...prev, googleCalendar: status }));
          return status;
        } catch {
          const status = { existing: res.status === 200 };
          setCalendarStatus(status);
          setSocial((prev) => ({ ...prev, googleCalendar: status }));
          return status;
        }
      } else {
        const status = { existing: false };
        setCalendarStatus(status);
        setSocial((prev) => ({ ...prev, googleCalendar: status }));
        return status;
      }
    } catch (error) {
      console.error("Error fetching calendar status:", error);
      const status = { existing: false };
      setCalendarStatus(status);
      setSocial((prev) => ({ ...prev, googleCalendar: status }));
      return status;
    }
  };

  const handleGoogleCalendarCodeExchange = async (code) => {
    try {
      setIsCalendarConnecting(true);
      const response = await authFetch(
        `${API}/calendar/google/token-exchange/`,
        {
          method: "POST",
          body: JSON.stringify({
            code,
            redirect_uri: `${window.location.origin}/profile`,
          }),
        },
      );
      if (response.ok) {
        await fetchCalendarStatus();
        setIsCalendarConnecting(false);
        toast.success(t("settings.calendarConnected"));
      } else {
        console.error("Token exchange failed:", response.status);
        setIsCalendarConnecting(false);
        toast.error(t("settings.calendarConnectionFailed"));
      }
    } catch (error) {
      console.error("Error exchanging Google OAuth code:", error);
      setIsCalendarConnecting(false);
      toast.error(t("settings.calendarError"));
    }
  };

  const disconnectInstagram = async () => {
    await authFetch(`${API}/auth/instagram-disconnect/`, { method: "DELETE" });
    fetchSocial();
  };

  const disconnectTelegram = async () => {
    await authFetch(`${API}/telegram/disconnect-telegram/`, {
      method: "DELETE",
    });
    fetchSocial();
  };

  const disconnectWhatsapp = async () => {
    // await authFetch(`${API}/whatsapp/disconnect-whatsapp/`, {
    //   method: "DELETE",
    // });
    deleteConnectWhatsAppDisconnect().then(() => {
      fetchSocial();
    });
  };

  const disconnectGoogleCalendar = async () => {
    try {
      const res = await authFetch(
        `${API}/calendar/google/calendar-disconnect/`,
        { method: "GET" },
      );
      if (res.ok) {
        await fetchCalendarStatus();
        toast.success(t("settings.calendarDisconnected"));
      } else {
        toast.error(t("settings.calendarDisconnectFailed"));
      }
    } catch {
      toast.error(t("settings.calendarErrorDisconnect"));
    }
  };

  const openDisconnectModal = (channel, channelName) =>
    setDisconnectModal({ isOpen: true, channel, channelName });
  const closeDisconnectModal = () =>
    setDisconnectModal({ isOpen: false, channel: null, channelName: "" });

  const handleDisconnectConfirm = () => {
    if (disconnectModal.channel === "instagram") disconnectInstagram();
    else if (disconnectModal.channel === "whatsapp") disconnectWhatsapp();
    else if (disconnectModal.channel === "telegram") disconnectTelegram();
    else if (disconnectModal.channel === "googleCalendar")
      disconnectGoogleCalendar();
    closeDisconnectModal();
  };

  const handleCalendarConnect = () => {
    if (!email) {
      toast.error(t("settings.pleaseWaitForUserData"));
      return;
    }
    setIsCalendarConnecting(true);
    window.location.href = googleCalendarUrl(email);
  };

  const isCalendarConnected = () => {
    if (!social.googleCalendar) return false;
    const status = social.googleCalendar;
    if (status && typeof status === "object")
      return status.existing === true || status.exists === true;
    return false;
  };

  const withSubCheck = (connectFn) => () => {
    if (!paymentCheck?.has_active_subscription) {
      setShowSubModal(true);
      return;
    }
    connectFn();
  };

  if (isLoading) {
    return (
      <div className="w-full bg-white rounded-2xl p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded-lg w-48 mb-8"></div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                <div className="flex-1">
                  <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const calendarConnected = isCalendarConnected();

  return (
    <>
      <AnimatePresence>
        {showSubModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSubModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl shadow-xl w-[340px] p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSubModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              >
                <X size={18} />
              </button>
              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 mb-4">
                <CreditCard size={22} className="text-amber-500" />
              </div>
              <h3 className="text-[16px] font-bold text-[#302B2B] mb-2">
                {t("settings.subscriptionRequired")}
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                {t("settings.subscriptionRequiredDesc")}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubModal(false)}
                  className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
                >
                  {t("cancel")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full bg-white rounded-2xl p-6 shadow-sm">
        <div className="mb-8">
          <h2 className="font-normal text-2xl text-gray-900 mb-2">
            {t("settings.connectedChannels")}
          </h2>
          <p className="text-gray-500 text-sm">
            {t("settings.manageIntegrations")}
          </p>
        </div>

        <div className="space-y-4 mb-10">
          <SectionHeader title={t("settings.socialMedias")} />

          <ChannelItem
            icon={<InstagramIcon connected={!!social.instagram} />}
            label="Instagram"
            description={t("settings.connectYourBusinessAccount")}
            connected={!!social.instagram}
            onConnect={withSubCheck(
              () => email && (window.location.href = instagramUrl(email)),
            )}
            onDisconnect={() => openDisconnectModal("instagram", "Instagram")}
          />

          <ChannelItem
            icon={<WhatsAppIcon connected={!!social.whatsapp} />}
            label="WhatsApp Business"
            description={t("settings.connectYourWhatsApp")}
            connected={!!social.whatsapp}
            onConnect={withSubCheck(() => setIsWhatsAppModalOpen(true))}
            onDisconnect={() => openDisconnectModal("whatsapp", "WhatsApp")}
          />

          <ChannelItem
            icon={<TelegramIcon connected={!!social.telegram} />}
            label="Telegram"
            description={t("settings.connectYourTelegram")}
            connected={!!social.telegram}
            onConnect={withSubCheck(openTelegramModal)}
            onDisconnect={() => openDisconnectModal("telegram", "Telegram")}
          />

          <ChannelItem
            icon={<FacebookIcon connected={false} />}
            label="Facebook"
            description={t("settings.facebookComingSoon")}
            disabled
          />
        </div>

        <div className="space-y-4">
          <SectionHeader title={t("settings.appsAndIntegrations")} />

          <ChannelItem
            icon={<CalendarIcon connected={calendarConnected} />}
            label="Google Calendar"
            description={t("settings.syncCalendar")}
            connected={calendarConnected}
            onConnect={withSubCheck(handleCalendarConnect)}
            onDisconnect={() =>
              openDisconnectModal("googleCalendar", "Google Calendar")
            }
            action={
              isCalendarConnecting
                ? t("settings.connecting")
                : calendarConnected
                  ? t("settings.connected")
                  : t("settings.connect")
            }
            isLoading={isCalendarConnecting}
          />
        </div>
      </div>

      <DisconnectModal
        isOpen={disconnectModal.isOpen}
        onClose={closeDisconnectModal}
        onConfirm={handleDisconnectConfirm}
        channelName={disconnectModal.channelName}
      />

      <TelegramConnectModal
        modal={telegramModal}
        locationCode={locationCode}
        phone={telegramPhone}
        otp={telegramOtp}
        password={telegramPassword}
        loading={telegramLoading}
        error={telegramError}
        onPhoneChange={(value) => {
          setTelegramPhone(value);
          if (telegramError) setTelegramError(null);
        }}
        onOtpChange={setTelegramOtp}
        onPasswordChange={setTelegramPassword}
        onPhoneSubmit={handleTelegramPhoneSubmit}
        onOtpSubmit={handleTelegramOtpSubmit}
        onPasswordSubmit={handleTelegramPasswordSubmit}
        onClose={closeTelegramModal}
      />

      <ConnectWhatsAppModal
        isOpen={isWhatsAppModalOpen}
        onClose={() => setIsWhatsAppModalOpen(false)}
        onSuccess={fetchSocial}
      />
    </>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      <span className="font-semibold text-gray-500 text-sm uppercase tracking-wider px-4">
        {title}
      </span>
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
    </div>
  );
}

function ChannelItem({
  icon,
  label,
  description,
  connected,
  action,
  disabled,
  isLoading,
  onConnect,
  onDisconnect,
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`group flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 rounded-2xl transition-all duration-300 hover:shadow-lg ${disabled ? "bg-gray-50/50 cursor-not-allowed" : connected ? "bg-gradient-to-r from-blue-50/30 to-[#020637]/5 hover:from-blue-50/50 hover:to-[#020637]/8" : "bg-gray-50/30 hover:bg-gray-50/50"}`}
    >
      <div className="flex items-center gap-4 min-w-0">
        {icon}
        <div className="min-w-0">
          <h3
            className={`font-semibold text-lg ${disabled ? "text-gray-400" : "text-gray-900"}`}
          >
            {label}
          </h3>
          <p
            className={`text-sm mt-0.5 ${disabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {description}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 sm:shrink-0">
        {disabled && (
          <span className="px-3 py-1.5 bg-gray-100 text-gray-400 text-sm font-medium rounded-lg">
            {t("settings.comingSoon")}
          </span>
        )}

        {connected && !disabled && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg">
              <FiCheck className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t("settings.connected")}
              </span>
            </div>
            <button
              onClick={onDisconnect}
              className="px-4 py-2.5 bg-transparent hover:bg-red-50 text-red-600 hover:text-red-700 rounded-xl font-medium text-sm flex items-center gap-2 transition-all duration-200 active:scale-[0.98]"
            >
              <VscDebugDisconnect className="w-4 h-4" />
              {t("settings.disconnect")}
            </button>
          </div>
        )}

        {!connected && !disabled && (
          <button
            onClick={onConnect}
            disabled={isLoading}
            className={`px-5 py-2.5 bg-gradient-to-r from-[#020637] to-blue-600 hover:from-[#020637] hover:to-blue-700 text-white font-medium rounded-xl shadow-md hover:shadow-lg hover:shadow-blue-100 flex items-center gap-2 transition-all duration-300 active:scale-[0.98] group/btn ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            <span>
              {isLoading
                ? t("settings.connecting")
                : action || t("settings.connect")}
            </span>
            {!isLoading && action !== t("settings.connecting") && (
              <SiGitconnected className="w-4 h-4 transition-transform duration-300 group-hover/btn:rotate-90" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function TelegramConnectModal({
  modal,
  locationCode,
  phone,
  otp,
  password,
  loading,
  error,
  onPhoneChange,
  onOtpChange,
  onPasswordChange,
  onPhoneSubmit,
  onOtpSubmit,
  onPasswordSubmit,
  onClose,
}) {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState(() => {
    return String(locationCode?.country_code || "US").toUpperCase();
  });
  const [nationalPhone, setNationalPhone] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  useEffect(() => {
    if (modal.isOpen) {
      setNationalPhone("");
      setIsPhoneValid(false);
      if (locationCode?.country_code) {
        setSelectedCountry(String(locationCode.country_code).toUpperCase());
      }
    }
  }, [modal.isOpen, locationCode?.country_code]);

  if (!modal.isOpen) return null;

  const stepConfig = {
    phone: {
      icon: <FiPhone className="w-7 h-7 text-blue-500" />,
      iconBg: "bg-blue-50",
      title: t("settings.telegramTitle"),
      subtitle: t("settings.telegramSubtitle"),
    },
    otp: {
      icon: (
        <img src={telegram} alt="Telegram" className="w-7 h-7 object-contain" />
      ),
      iconBg: "bg-blue-50",
      title: t("settings.telegramOtpTitle"),
      subtitle: modal.message || t("settings.telegramOtpSubtitle"),
    },
    "2fa": {
      icon: <FiLock className="w-7 h-7 text-[#020637]" />,
      iconBg: "bg-[#020637]/5",
      title: t("settings.telegram2faTitle"),
      subtitle: modal.message || t("settings.telegram2faSubtitle"),
    },
  };

  const cfg = stepConfig[modal.step];

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (modal.step === "phone") onPhoneSubmit();
    else if (modal.step === "otp") onOtpSubmit();
    else if (modal.step === "2fa") onPasswordSubmit();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3" />
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="text-center mb-6">
          <div
            className={`w-16 h-16 ${cfg.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}
          >
            {cfg.icon}
          </div>
          <h3 className="font-bold text-2xl text-gray-900 mb-2">{cfg.title}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            {cfg.subtitle}
          </p>
        </div>

        <div className="space-y-4">
          {modal.step === "phone" && (
            <PhoneField
              phone={nationalPhone}
              country={selectedCountry}
              label={false}
              containerHeight="h-12"
              error={error}
              required={false}
              placeholder={t("settings.telegramPhonePlaceholder")}
              onPhoneChange={(val, valid, e164) => {
                setNationalPhone(val);
                setIsPhoneValid(valid);
                onPhoneChange(e164);
              }}
              onCountryChange={(c) => {
                setSelectedCountry(c);
              }}
              onKeyDown={handleKeyDown}
            />
          )}

          {modal.step === "otp" && (
            <input
              type="text"
              value={otp}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 5);
                onOtpChange(val);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("settings.telegramOtpPlaceholder")}
              maxLength={5}
              autoFocus
              className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition text-gray-800 text-center text-xl tracking-widest font-mono"
            />
          )}

          {modal.step === "2fa" && (
            <input
              type="password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("settings.telegram2faPlaceholder")}
              autoFocus
              className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/15 outline-none transition text-gray-800"
            />
          )}

          {error && modal.step !== "phone" && (
            <p className="text-sm text-red-500 text-center">{error}</p>
          )}

          <button
            onClick={
              modal.step === "phone"
                ? onPhoneSubmit
                : modal.step === "otp"
                  ? onOtpSubmit
                  : onPasswordSubmit
            }
            disabled={
              loading ||
              (modal.step === "phone" && !isPhoneValid) ||
              (modal.step === "otp" && otp.length < 5) ||
              (modal.step === "2fa" && !password.trim())
            }
            className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t("settings.verifying")}</span>
              </>
            ) : (
              <span>
                {modal.step === "phone"
                  ? t("settings.sendCode")
                  : modal.step === "otp"
                    ? t("settings.verifyCode")
                    : t("settings.confirm")}
              </span>
            )}
          </button>
        </div>

        <div className="flex justify-center mt-4">
          <div className="flex gap-2">
            {(modal.step === "2fa"
              ? ["phone", "otp", "2fa"]
              : ["phone", "otp"]
            ).map((s, i, arr) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all duration-300 ${modal.step === s ? "bg-blue-500 w-4" : arr.indexOf(modal.step) > i ? "bg-blue-200 w-2" : "bg-gray-200 w-2"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
